import { AttributeComponentProps } from "@coltorapps/builder-react";
import { gridHeaderAttribute } from "../definition";
import { NumberType } from "../definition/number";
import { DateType } from "../definition/date";
import { DropdownType } from "../definition/dropdown";
import { RowExpressionType } from "../definition/row-expression";
import { InputType } from "../definition/input";

export type GridHeadersValue = AttributeComponentProps<
  typeof gridHeaderAttribute
>["attribute"]["value"];
export type GridHeaderItem = GridHeadersValue[number];

export type StaticOption = {
  label: string;
  value: string;
  key: string;
};

export type StaticDatasource = {
  type: "static";
  options: StaticOption[];
  defaultValue?: {
    isReference: boolean;
    value?: string;
    reference?: string;
  };
  displayOption?: "label" | "label_value";
};

export type DynamicDatasourceFilterUi = {
  isReference: false;
  columnKey?: string;
  logic?: string;
  formField?: string;
};

export type DynamicDatasourceFilterExpression = {
  isReference: true;
  expression?: string;
};

export type DynamicDatasource = {
  type: "dynamic";
  table?: {
    tableKey: string | null;
    labelKey: string | null;
    valueKey: string | null;
  };
  sorter?: {
    columnKey?: string;
    order?: "asc" | "desc";
  };
  filter?: DynamicDatasourceFilterUi | DynamicDatasourceFilterExpression;
  displayOption?: "label" | "label_value";
};

// Union of partials — allows type-specific fields (e.g. datasource, expression)
export type GridHeaderItemPatch<T extends GridHeaderItem = GridHeaderItem> =
  Partial<T>;

export type HeaderTypeComponentProps<
  T extends GridHeaderItem = GridHeaderItem,
> = {
  header: T;
  updateHeader: (patch: GridHeaderItemPatch<T>) => void;
  replaceHeader: (nextHeader: T) => void;
  headerError?: string;
};

export type InputHeaderType = HeaderTypeComponentProps<InputType>;
export type NumberHeaderType = HeaderTypeComponentProps<NumberType>;
export type DateHeaderType = HeaderTypeComponentProps<DateType>;
export type DropdownHeaderType = HeaderTypeComponentProps<DropdownType>;
export type RowExpressionHeaderType =
  HeaderTypeComponentProps<RowExpressionType>;

export const createStaticOption = (
  index: number,
  keyValue: string,
): StaticOption => ({
  label: `Option ${index + 1}`,
  value: `option_${index + 1}_${keyValue || "column"}`,
  key: crypto.randomUUID(),
});

export const normalizeStaticDatasource = (
  datasource: unknown,
  keyValue: string,
): StaticDatasource => {
  if (
    datasource &&
    typeof datasource === "object" &&
    "type" in datasource &&
    (datasource as { type?: string }).type === "static"
  ) {
    const staticDatasource = datasource as unknown as {
      options?: unknown[];
      defaultValue?: unknown;
    };
    const rawOptions = Array.isArray(staticDatasource.options)
      ? staticDatasource.options
      : [];

    const options = rawOptions.length
      ? rawOptions.map((option, index) => {
          const entry = option as Partial<StaticOption>;
          return {
            label:
              typeof entry.label === "string"
                ? entry.label
                : `Option ${index + 1}`,
            value:
              typeof entry.value === "string"
                ? entry.value
                : `option_${index + 1}_${keyValue || "column"}`,
            key:
              typeof entry.key === "string" && entry.key
                ? entry.key
                : crypto.randomUUID(),
          };
        })
      : [createStaticOption(0, keyValue)];

    const rawDefaultValue = staticDatasource.defaultValue;
    const defaultValue =
      rawDefaultValue &&
      typeof rawDefaultValue === "object" &&
      "isReference" in rawDefaultValue
        ? (rawDefaultValue as StaticDatasource["defaultValue"])
        : undefined;

    return { type: "static", options, defaultValue };
  }

  return {
    type: "static",
    options: [createStaticOption(0, keyValue)],
    defaultValue: undefined,
  };
};
